'use client';

import { RenderError } from '@/react/components/errors/devtools/RenderError';
import { TopErrorManagerContext } from '@/react/components/errors/devtools/TopErrorManagerContext';
import { useConfig } from '@/react/hooks/useConfig';
import { useEffectEvent } from '@/react/hooks/useEffectEvent';
import { EnvironmentService } from '@/services/isomorphic/EnvironmentService';
import { type FC, use, useEffect } from 'react';
import { isNullish } from '@/utils/null-utils';

import S from '@/react/components/content/core/Content/styles.base.module.scss';

/** A pair of a content wrapper element and the number of high priority images in it. */
interface IContentImageCountPair {
  /** The content wrapper element. */
  wrapper: Element;
  /** The number of high priority images in the content wrapper. */
  count: number;
}

/**
 * A component that reports a warning if the amount of high priority images exceeds the configured threshold.
 * It works by inspecting the rendered subtree for images with `fetchpriority="high"`.
 */
const NonProdHighPriorityImagesWarning: FC = () => {
  const builderConfig = useConfig('builderio');
  const { addError } = use(TopErrorManagerContext);

  /**
   * An effect event callback which reports an error if the amount of high priority
   * images exceeds the configured threshold.
   * It works by inspecting the rendered subtree for images with `fetchpriority="high"`.
   */
  const addErrorOnThresholdExceeded = useEffectEvent(
    (contentImageCountPairs: Array<IContentImageCountPair>) => {
      const maxAmountOfHighPriorityMedia = builderConfig.getSetting(
        'maxAmountOfHighPriorityMedia'
      ).value;

      const totalCount = contentImageCountPairs.reduce(
        (acc, { count }) => acc + count,
        0
      );

      if (totalCount > maxAmountOfHighPriorityMedia) {
        const details = contentImageCountPairs.map(({ wrapper, count }) => {
          const contentType = wrapper.getAttribute('data-content-type');
          const contentName = wrapper.getAttribute('data-content-name');
          return `Type: "${contentType}", Name: ${isNullish(contentName) ? 'N/A' : `"${contentName}"`}, Count: ${count}`;
        });

        const errorMessage = `The total amount of high priority images exceeds the allowed amount of ${maxAmountOfHighPriorityMedia}.\n\n${details.join(',\n')}`;

        addError(new RenderError({ error: new Error(errorMessage) }));
      }
    }
  );

  useEffect(function checkForHighPriorityImagesExceedingThreshold() {
    const reportIfHighPriorityImagesExceedThreshold = (): void => {
      const contentWrappers = Array.from(
        document.body.querySelectorAll(`.${S.contentWrapper}`)
      );

      const contentImageCountPairs = contentWrappers
        .map((wrapper) => {
          // Count the number of high priority images in the content wrapper.
          const count = Array.from(
            wrapper.querySelectorAll('img[fetchpriority="high"]')
          ).length;
          return { wrapper, count };
        })
        .filter(({ count }) => count > 0);

      addErrorOnThresholdExceeded(contentImageCountPairs);
    };

    // If the page has already finished loading (e.g. cached resources,
    // client-side navigation, or fast loads), the `load` event will never
    // fire again, so we run the check immediately.
    if (document.readyState === 'complete') {
      reportIfHighPriorityImagesExceedThreshold();
      return undefined;
    }

    window.addEventListener('load', reportIfHighPriorityImagesExceedThreshold, {
      once: true
    });

    return () => {
      window.removeEventListener(
        'load',
        reportIfHighPriorityImagesExceedThreshold
      );
    };
  }, []);

  return null;
};

/**
 * A component that reports a warning in non-production environments
 * if the amount of high priority images exceeds the configured threshold.
 * @returns A React node.
 */
export const HighPriorityImagesWarning: FC = (process.env.NEXT_PUBLIC_APP_ENV === "prod")
  ? () => null
  : NonProdHighPriorityImagesWarning; // TODO: consider using a key to force a re-mount when the pathname changes
